import { computed, ref, watch } from 'vue';
import {
  useEventListener,
  useResizeObserver,
  useWindowSize,
} from '@vueuse/core';

export function useConversationListResize(
  listRef,
  uiSettings,
  updateUISettings
) {
  const { width: viewportWidth } = useWindowSize();
  const parentWidth = ref(0);
  const savedWidth = ref(Number(uiSettings.value.conversation_list_width) || 0);
  const dragWidth = ref(null);
  const isResizing = ref(false);
  const minWidth = 280;
  const maxWidth = computed(() =>
    Math.max(
      minWidth,
      Math.min(640, (parentWidth.value || viewportWidth.value) - 360)
    )
  );
  const defaultWidth = computed(() =>
    viewportWidth.value >= 1536 ? 412 : 340
  );
  const clamp = value =>
    Math.round(Math.max(minWidth, Math.min(maxWidth.value, value)));
  const width = computed(() =>
    clamp(dragWidth.value ?? (savedWidth.value || defaultWidth.value))
  );
  let startX = 0;
  let startWidth = 0;
  let direction = 1;

  useResizeObserver(
    computed(() => listRef.value?.parentElement),
    entries => {
      parentWidth.value = entries[0]?.contentRect.width || 0;
    }
  );
  watch(
    () => uiSettings.value.conversation_list_width,
    value => {
      savedWidth.value = Number(value) || 0;
    }
  );

  const save = value => {
    savedWidth.value = clamp(value);
    updateUISettings({ conversation_list_width: savedWidth.value });
  };
  const startResize = event => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    startX = event.clientX;
    startWidth = width.value;
    direction = getComputedStyle(listRef.value).direction === 'rtl' ? -1 : 1;
    isResizing.value = true;
  };
  const finishResize = () => {
    if (!isResizing.value) return;
    save(width.value);
    isResizing.value = false;
    dragWidth.value = null;
  };
  const cancelResize = () => {
    isResizing.value = false;
    dragWidth.value = null;
  };
  const resetWidth = () => {
    cancelResize();
    save(defaultWidth.value);
  };
  const onKeydown = event => {
    const rtl = getComputedStyle(listRef.value).direction === 'rtl';
    const actions = {
      ArrowLeft: () => save(width.value + (rtl ? 20 : -20)),
      ArrowRight: () => save(width.value + (rtl ? -20 : 20)),
      Home: () => save(minWidth),
      End: () => save(maxWidth.value),
      Enter: resetWidth,
      Escape: cancelResize,
    };
    if (!actions[event.key]) return;
    event.preventDefault();
    actions[event.key]();
  };
  useEventListener(window, 'pointermove', event => {
    if (isResizing.value)
      dragWidth.value = clamp(
        startWidth + (event.clientX - startX) * direction
      );
  });
  useEventListener(window, 'pointerup', finishResize);
  useEventListener(window, 'pointercancel', cancelResize);
  useEventListener(window, 'blur', cancelResize);

  return {
    width,
    minWidth,
    maxWidth,
    isResizing,
    startResize,
    resetWidth,
    onKeydown,
  };
}
